import React, { useCallback, useMemo, useState } from 'react';

import { useExport, ExportFormats } from '@qualio/export-components';
import { getQRI } from '@qualio/search-qri';
import {
  QText,
  QBox,
  QButton,
  QButtonGroup,
  QHeading,
  QModal,
  QSpinner,
  QTag,
  QModalHeader,
  QModalBody,
  QModalActions,
  QCloseButton,
  QMenuButton,
  QMenuItem,
  useCurrentUser,
  QRouteTabs,
  QResourceRouteTabs,
  QNotFound,
} from '@qualio/ui-components';
import { exportApi } from 'api';
import {
  useSupplierArchiveMutation,
  useSupplierQuery,
  useSupplierUnArchiveMutation,
  useSupplierExportMutation,
  useRevertSupplierToDraftMutationType,
} from 'hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { STATUS_CELL_MAPPING } from 'lib';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Supplier, SupplierStatus } from 'types';
import { QRI_SERVICE_PUBLIC_URL, routes } from 'utils';
import { ChangeControlAlerts, StatusChangeModal } from 'v2components';

import {
  ALLOWED_STATUSES_FOR_ARCHIVE,
  ALLOWED_STATUSES_FOR_EDIT,
  ALLOWED_STATUSES_FOR_REVERT_TO_DRAFT,
  ALLOWED_STATUSES_FOR_UNARCHIVE,
} from './SupplierDetailsView.const';

type OutletSupplierType = {
  supplier: Supplier;
  isLoading: boolean;
  refetchSupplier: () => void;
};

const SupplierContext = React.createContext<OutletSupplierType | undefined>(undefined);

export const SupplierDetailsView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { supplierId } = useParams<{ supplierId: string }>();
  const { supplier, refetchSupplier, isSupplierLoading, error, isError } = useSupplierQuery(supplierId!);
  const [modal, setModal] = useState<any | null>(null);

  const { supplierExport: newSupplierExportEnabled } = useFlags();
  const { companyId } = useCurrentUser();

  const onModalSuccess = useCallback(() => {
    setModal(null);
    refetchSupplier();
  }, [refetchSupplier]);

  const [isStatusChangeModalVisible, setIsChangeStatusModalVisible] = useState<string | undefined>(undefined);

  const { isArchiveSupplierLoading, archiveSupplierMutate } = useSupplierArchiveMutation(onModalSuccess);

  const { isExportSupplierLoading, exportSupplierMutate } = useSupplierExportMutation(onModalSuccess);

  const { trigger: handleTriggerExport, isLoading: isFesExportLoading } = useExport(exportApi, {
    toastTitle: `${supplier?.name || 'Supplier'} export`,
    'data-cy': 'supplier-export',
  });

  const isExportLoading = useMemo(() => {
    return newSupplierExportEnabled ? isFesExportLoading : isExportSupplierLoading;
  }, [newSupplierExportEnabled, isFesExportLoading, isExportSupplierLoading]);

  const handleExport = useCallback(
    (supplier: Supplier) => {
      if (!newSupplierExportEnabled) {
        return exportSupplierMutate(supplier);
      }

      handleTriggerExport({
        exports: [
          {
            format: ExportFormats.fetch_payload,
            data: {
              export_payload: `${process.env.REACT_APP_API_BASE_URL}/company/${companyId}/sqm/supplier/${supplier.supplier}/export-payload`,
              resource_description: `Supplier ${supplier.name || ''} export`,
            },
          },
        ],
        bundle_filename: `Supplier ${supplier.name || 'Supplier'}`,
        metadata: {
          domain: 'supplier',
          resource_name: `${supplier.name || 'Supplier'} export`,
        },
      });
      onModalSuccess();
    },
    [companyId, newSupplierExportEnabled, exportSupplierMutate, handleTriggerExport, onModalSuccess],
  );

  const { isUnArchiveSupplierLoading, unArchiveSupplierMutate } = useSupplierUnArchiveMutation(onModalSuccess);
  const { isRevertSupplierToDraftLoading, revertSupplierToDraftMutate } =
    useRevertSupplierToDraftMutationType(onModalSuccess);

  const modalVariations = useMemo(
    () => ({
      archive: {
        title: t('supplierDetails.modals.archive.title'),
        description: (
          <>
            <QText>{t('supplierDetails.modals.archive.message')}</QText>
          </>
        ),
        buttonProps: {
          onClick: () => supplier && archiveSupplierMutate(supplier),
          variant: 'solid',
          isDestructive: true,
          children: t('supplierDetails.buttons.archive'),
          isLoading: isArchiveSupplierLoading,
          'data-cy': 'archive-modal-confirm',
        },
      },
      unarchive: {
        title: t('supplierDetails.modals.unarchive.title'),
        description: (
          <>
            <QText>{t('supplierDetails.modals.unarchive.message')}</QText>
          </>
        ),
        buttonProps: {
          onClick: () => supplier && unArchiveSupplierMutate(supplier),
          variant: 'solid',
          children: t('supplierDetails.buttons.unarchive'),
          isLoading: isUnArchiveSupplierLoading,
          'data-cy': 'unarchive-modal-confirm',
        },
      },
      revertToDraft: {
        title: t('supplierDetails.modals.revertToDraft.title'),
        description: <QText>{t('supplierDetails.modals.revertToDraft.message')}</QText>,
        buttonProps: {
          onClick: () => supplier && revertSupplierToDraftMutate(supplier),
          variant: 'solid',
          children: t('supplierDetails.buttons.revertToDraft'),
          isLoading: isRevertSupplierToDraftLoading,
          'data-cy': 'revert-to-draft-modal-confirm',
        },
      },
      export: {
        title: t('supplierDetails.modals.export.title'),
        description: <QText>{t('supplierDetails.modals.export.message')}</QText>,
        buttonProps: {
          onClick: () => supplier && handleExport(supplier),
          isLoading: isExportLoading,
          variant: 'solid',
          children: t('supplierDetails.buttons.export'),
          'data-cy': 'export-supplier-modal-confirm',
        },
      },
    }),
    [
      t,
      isArchiveSupplierLoading,
      isUnArchiveSupplierLoading,
      isRevertSupplierToDraftLoading,
      isExportLoading,
      supplier,
      archiveSupplierMutate,
      unArchiveSupplierMutate,
      revertSupplierToDraftMutate,
      handleExport,
    ],
  );

  const showApproverBadge = (status = 'DRAFT') => {
    const tag = STATUS_CELL_MAPPING[status];
    return <QTag variantColor={tag.tagColor}>{tag.text}</QTag>;
  };

  const combinedLoadingState =
    isArchiveSupplierLoading || isUnArchiveSupplierLoading || isRevertSupplierToDraftLoading || isExportLoading;

  const showStatusChangeModal = (status: string) => {
    setIsChangeStatusModalVisible(status);
  };

  const hideStatusChangeModal = () => {
    setIsChangeStatusModalVisible(undefined);
  };

  if (isError && error && !isSupplierLoading) {
    const statusCode = error.response?.status;

    if (statusCode === 404) {
      return <QNotFound data-cy="supplier-details-not-found" />;
    }
  }

  return (
    <QBox display="flex" flexDirection="column">
      {!!supplier && (
        <QButton leftIcon={'ArrowLeft'} onClick={() => navigate(`/${routes.supplierList}`)} variant="link" size="sm">
          {t('supplierDetails.buttons.back')}
        </QButton>
      )}
      <QBox display="flex" flexDirection="row" justifyContent="space-between" mt={4} mb={4}>
        {!isSupplierLoading ? (
          <span>
            <QHeading as="h1" size="lg">
              {supplier?.name}
            </QHeading>
            {showApproverBadge(supplier?.status)}
          </span>
        ) : null}
        {supplier?.status ? (
          <QButtonGroup>
            <QMenuButton data-cy="details-menu" buttonLabel="menu" variant="icon">
              <QMenuItem
                data-cy="supplier-export-button"
                onClick={() => setModal(modalVariations.export)}
                isDisabled={isExportLoading}
              >
                {t('supplierDetails.buttons.export')}
              </QMenuItem>
              {ALLOWED_STATUSES_FOR_ARCHIVE.includes(supplier?.status) ? (
                <QMenuItem data-cy="supplier-archive-button" onClick={() => setModal(modalVariations.archive)}>
                  {t('supplierDetails.buttons.archive')}
                </QMenuItem>
              ) : null}
              {ALLOWED_STATUSES_FOR_UNARCHIVE.includes(supplier?.status) ? (
                <QMenuItem onClick={() => setModal(modalVariations.unarchive)} data-cy="supplier-unarchive-button">
                  {t('supplierDetails.buttons.unarchive')}
                </QMenuItem>
              ) : null}
            </QMenuButton>

            {supplier && ALLOWED_STATUSES_FOR_EDIT.includes(supplier?.status) ? (
              <QButton onClick={() => navigate(`/${routes.editSupplier}/${supplierId}`)} data-cy="supplier-edit-button">
                {t('supplierDetails.buttons.edit')}
              </QButton>
            ) : null}
            {ALLOWED_STATUSES_FOR_REVERT_TO_DRAFT.includes(supplier?.status) ? (
              <QButton
                onClick={() => setModal(modalVariations.revertToDraft)}
                data-cy="supplier-revert-to-draft-button"
              >
                {t('supplierDetails.buttons.revertToDraft')}
              </QButton>
            ) : null}
          </QButtonGroup>
        ) : null}
      </QBox>
      <QBox mb={4}>
        <ChangeControlAlerts
          changeControl={supplier?.changeControl}
          supplierStatus={supplier?.status as SupplierStatus}
          handleStatusChangeModal={showStatusChangeModal}
        />
      </QBox>

      {!supplier && (
        <QBox w="100%" textAlign="center" p={5}>
          <QSpinner />
        </QBox>
      )}

      {!!supplier && (
        <SupplierContext.Provider value={{ supplier, isLoading: isSupplierLoading, refetchSupplier }}>
          <QBox data-cy="supplier-details-tabs">
            <QResourceRouteTabs
              data-cy="supplier-details-tabs"
              qri={[
                getQRI({
                  qriBaseUrl: QRI_SERVICE_PUBLIC_URL,
                  domain: 'suppliers',
                  domainType: 'supplier',
                  companyId: companyId.toString(),
                  id: supplierId!,
                }),
              ]}
            >
              <QRouteTabs.Item route={routes.supplierDetails.detailsTab} data-cy="details-tab">
                {t('supplierDetails.tabs.details')}
              </QRouteTabs.Item>
              <QRouteTabs.Item route={routes.supplierDetails.supportingDocumentsTab} data-cy="documents-tab">
                {t('supplierDetails.tabs.supportingDocuments')}
              </QRouteTabs.Item>
              <QRouteTabs.Item route={routes.supplierDetails.auditsTab} data-cy="audits-tab">
                {t('supplierDetails.tabs.audits')}
              </QRouteTabs.Item>
            </QResourceRouteTabs>
          </QBox>
        </SupplierContext.Provider>
      )}
      {modal ? (
        <QModal isOpen={!!modal} onClose={() => setModal(null)}>
          <QModalHeader>
            <QText>{modal.title}</QText>
            <QCloseButton onClick={() => setModal(null)} />
          </QModalHeader>
          <QModalBody>{modal.description}</QModalBody>
          <QModalActions>
            <QButton onClick={() => setModal(null)} variant="outline">
              {t('common.buttons.cancel')}
            </QButton>
            <QButton {...modal.buttonProps} data-cy={modal.buttonProps['data-cy']} isLoading={combinedLoadingState} />
          </QModalActions>
        </QModal>
      ) : null}

      <StatusChangeModal
        isVisible={!!isStatusChangeModalVisible}
        onClose={hideStatusChangeModal}
        onSuccess={() => {
          setIsChangeStatusModalVisible(undefined);
          refetchSupplier();
        }}
        current={supplier}
        status={isStatusChangeModalVisible}
      />
    </QBox>
  );
};

export const useRouterOutletSupplier = (): OutletSupplierType => {
  const context = React.useContext(SupplierContext);
  if (context === undefined) {
    throw new Error('useRouterOutletSupplier must be used within SupplierDetailsView');
  }
  return context;
};
